/**
 * Utility functions for HTML document structure detection and manipulation
 * Used to preserve complete HTML documents with <html>, <head>, <style>, <body> tags
 * when editing in TinyMCE
 */

export interface DocumentMetadata {
  headContent: string;
  htmlAttributes: Record<string, string>;
  hasDocumentStructure: boolean;
}

/**
 * Detects if the given HTML string is a complete HTML document
 * @param html - HTML string to check
 * @returns true if the HTML contains document structure tags
 */
export function isCompleteHtmlDocument(html: string): boolean {
  if (!html || typeof html !== 'string') {
    return false;
  }
  
  const lowerHtml = html.trim().toLowerCase();
  
  // Check for presence of <html>, (<head> or <style>), and <body> tags
  return (
    lowerHtml.includes('<html') &&
    (lowerHtml.includes('<head') || lowerHtml.includes('<style')) &&
    lowerHtml.includes('<body')
  );
}

/**
 * Extracts document structure metadata from a complete HTML document
 * @param html - Complete HTML document string
 * @returns Object containing body content and metadata
 */
export function unwrapDocumentStructure(html: string): {
  bodyContent: string;
  metadata: DocumentMetadata;
} {
  if (!isCompleteHtmlDocument(html)) {
    return {
      bodyContent: html,
      metadata: {
        headContent: '',
        htmlAttributes: {},
        hasDocumentStructure: false
      }
    };
  }

  try {
    // Parse HTML using DOMParser
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Extract head content (especially style tags)
    const headElement = doc.querySelector('head');
    const headContent = headElement?.innerHTML || '';

    // Extract body content
    const bodyElement = doc.querySelector('body');
    const bodyContent = bodyElement?.innerHTML || html;

    // Extract html attributes
    const htmlElement = doc.documentElement;
    const htmlAttributes: Record<string, string> = {};
    
    if (htmlElement && htmlElement.attributes) {
      for (let i = 0; i < htmlElement.attributes.length; i++) {
        const attr = htmlElement.attributes[i];
        htmlAttributes[attr.name] = attr.value;
      }
    }

    return {
      bodyContent,
      metadata: {
        headContent,
        htmlAttributes,
        hasDocumentStructure: true
      }
    };
  } catch (error) {
    console.error('Error unwrapping document structure:', error);
    return {
      bodyContent: html,
      metadata: {
        headContent: '',
        htmlAttributes: {},
        hasDocumentStructure: false
      }
    };
  }
}

/**
 * Reconstructs a complete HTML document from body content and metadata
 * @param bodyContent - HTML content for the body
 * @param metadata - Document structure metadata
 * @returns Complete HTML document string
 */
export function wrapDocumentStructure(
  bodyContent: string,
  metadata: DocumentMetadata
): string {
  if (!metadata.hasDocumentStructure) {
    return bodyContent;
  }

  // Serialize html attributes
  const htmlAttributesString = Object.entries(metadata.htmlAttributes)
    .map(([key, value]) => `${key}="${value}"`)
    .join(' ');

  const htmlTag = htmlAttributesString
    ? `<html ${htmlAttributesString}>`
    : '<html>';

  return `${htmlTag}
<head>${metadata.headContent}</head>
<body>${bodyContent}</body>
</html>`;
}
